import { Box, Button, Heading, Text, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout, Logo } from 'components/atoms'
import { InputBuild } from 'components/molecules'
import { useAuthContext } from 'contexts/auth'
import { environments } from 'environments/environments'
import { AuthRequestForgotPassword } from 'models'
import React, { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useForm } from 'react-hook-form'
import { Link as LinkRouter, useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import { CustomError } from '../SignIn/interface'
import { errorsAuth } from '../utils/errorsAuth'

const Forgot: React.FC = () => {
    const navigate = useNavigate()
    const { currentUser, forgot, validateCaptcha } = useAuthContext()
    const toast = useToast()
    const { CAPTCHA_SITE_KEY } = environments

    const [captcha, setCaptcha] = useState('')

    const recaptchaRef = useRef<ReCAPTCHA>(null)

    const schema = yup
        .object({
            email: yup.string().required()
        })
        .required()

    const {
        handleSubmit,
        register,
        formState: { errors, isSubmitting }
    } = useForm<AuthRequestForgotPassword>({
        resolver: yupResolver(schema)
    })

    const validateForgotCaptcha = async (values: AuthRequestForgotPassword) => {
        if (process.env.REACT_APP_ENV === 'qa') {
            return forgotPassword(values)
        }

        if (captcha) {
            validateCaptcha(captcha)
                .then(() => {
                    return forgotPassword(values)
                })
                .catch((error: CustomError) => {
                    error.response.data
                        .filter(
                            (elem, index, arr) =>
                                index ===
                                arr.findIndex(
                                    checkEle => checkEle.codigo === elem.codigo
                                )
                        )
                        .map(res => {
                            const error = errorsAuth[res.codigo]
                            toast({
                                title: error.title,
                                description: error.description,
                                position: 'bottom-left',
                                status: 'error',
                                isClosable: true,
                                variant: 'solid-error'
                            })
                        })
                })
        } else {
            toast({
                title: 'Erro!',
                description: 'Por favor, confirme que você não é um robô.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true,
                variant: 'solid-error'
            })
        }
    }

    const forgotPassword = (values: AuthRequestForgotPassword) => {
        forgot(values)
            .then(() => {
                toast({
                    title: 'Nova senha a caminho!',
                    description: 'Verifique sua caixa de entrada',
                    position: 'bottom-left',
                    status: 'info',
                    isClosable: true
                })
                navigate('/recuperar-senha')
            })
            .catch((error: CustomError) => {
                recaptchaRef.current?.reset()

                error.response.data
                    .filter(
                        (elem, index, arr) =>
                            index ===
                            arr.findIndex(
                                checkEle => checkEle.codigo === elem.codigo
                            )
                    )
                    .map(res => {
                        const error = errorsAuth[res.codigo]
                        toast({
                            title: error.title,
                            description: error.description,
                            position: 'bottom-left',
                            status: 'error',
                            isClosable: true,
                            variant: 'solid-error'
                        })
                    })
            })
    }

    const onSubmit = async (values: AuthRequestForgotPassword) => {
        validateForgotCaptcha(values)
    }

    useEffect(() => {
        currentUser && navigate('/entrar')
    }, [currentUser])

    return (
        <Box
            data-testid="forgot"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            color="blackAlpha.900"
            bgColor="backdrop"
            h="100vh"
            w="100%"
        >
            <Box display="grid" w="100%" maxW="sm" gap={8}>
                <Box>
                    <Logo maxW={187} />
                </Box>
                <Box
                    data-testid="forgot-content"
                    bg="white"
                    borderRadius="lg"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 12 }}
                >
                    <Heading
                        data-testid="forgot-heading"
                        size="md"
                        textAlign="center"
                        mb="2"
                    >
                        Recuperação de senha
                    </Heading>
                    <Text
                        data-testid="forgot-text"
                        fontSize="lg"
                        textAlign="center"
                    >
                        Preencha os dados para continuar
                    </Text>
                    <form
                        data-testid="forgot-form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Layout
                            data-testid="forgot-inputs"
                            type="container"
                            full
                            px={6}
                            mt={8}
                        >
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                <Layout
                                    data-testid="forgot-input"
                                    type="col"
                                    layout={{ base: 12 }}
                                >
                                    <InputBuild
                                        id="email"
                                        label="Usuário ou e-mail"
                                        required
                                        err={errors && errors.email}
                                        {...register('email')}
                                    />
                                </Layout>
                                <Layout type="col" layout={{ base: 12 }}>
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        data-testid="captcha-test"
                                        sitekey={CAPTCHA_SITE_KEY}
                                        onChange={e => e && setCaptcha(e)}
                                    />
                                </Layout>

                                <Layout
                                    data-testid="forgot-action"
                                    type="col"
                                    layout={{ base: 12 }}
                                >
                                    <Button
                                        data-testid="forgot-submit"
                                        w="full"
                                        colorScheme="primary"
                                        size="md"
                                        type="submit"
                                        isLoading={isSubmitting}
                                    >
                                        Enviar
                                    </Button>
                                </Layout>
                                <Layout type="col" layout={{ base: 12 }} mt={4}>
                                    <Button
                                        data-testid="forgot-cancel"
                                        as={LinkRouter}
                                        size="sm"
                                        w="full"
                                        colorScheme="primary"
                                        variant="outline"
                                        to="/entrar"
                                    >
                                        Voltar
                                    </Button>
                                </Layout>
                                <Text
                                    display="flex"
                                    color="#888891"
                                    fontSize="1rem"
                                    justifyContent="center"
                                >
                                    Versão: {process.env.REACT_APP_VERSION}
                                </Text>
                            </Layout>
                        </Layout>
                    </form>
                </Box>
            </Box>
        </Box>
    )
}

export default Forgot
